import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { List, Map } from "immutable";
import debounce from "lodash/debounce";
import { Form } from "formsy-react";
import IconButton from "material-ui/IconButton";
import { ListItem } from "material-ui/List";
import Link from "@material-ui/core/Link";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import { List as VirtualizedList } from "react-virtualized/dist/commonjs/List";
import Dialog from "components/dialog";
import Button from "components/form/button";
import TextField from "components/form/text-field";
import ImageIcon from "components/icons/image";
import Trash from "components/icons/trash";
import theme from "styles/theme";
import { getValueByKey } from "helpers/product-collection";
import { DATATEST } from "constants/integration";
import { SEARCH_PRODUCTS_MAX_LENGTH } from "constants/product";

const REACT_VIRTUALIZED_CONSTANTS = {
    infiniteLoader: {
        threshold: 1,
        rowCount: 100,
    },
    list: {
        height: 256,
        width: 452,
    },
};
const IMAGE_HEIGHT = 72;
function keyPath(field) {
    return ["fields", field, "string_value"];
}

enum postions {
    ABSOLUTE = "absolute",
}
const INPUT_BOX_PADDING = 24;
const DIALOG_MAX_WIDTH = 500;
const DIALOG_PADDING = 100;
interface IProps {
    merchantId: string;
    brandId?: string;
    isFetching: boolean;
    globalProducts: List<any>;
    globalProductState?: any;
    itemDisplayProperty?: List<any>;
    onCancel: () => void;
    onFetchGlobalProducts: (params) => void;
    onFetchNextGlobalProducts: (params) => void;
    onResetGlobalProducts: (params) => void;
    onSubmit: (id) => void;
    cardViewFields: any;
    setViewFullDetailProductId: (id: string) => void;
    handleOpenIsViewFullDetail: () => void;
}

interface IState {
    anchorEl?: any;
    filters: Map<any, any>;
    globalProducts: any[];
    isPopoverOpen: boolean;
    product: any;
    productId: string;
}

const initialState = () => ({
    anchorEl: undefined,
    filters: Map(),
    globalProducts: undefined,
    isPopoverOpen: false,
    product: null,
    productId: "",
});

export class LinkProduct extends React.PureComponent<IProps, IState> {
    _form: any;
    container: any;
    infiniteLoader: any;
    cache = new CellMeasurerCache({
        defaultHeight: 120,
        fixedWidth: true,
        minHeight: 120,
    });
    state = initialState();
    muiStyles = {
        dialog: {
            bodyStyle: { padding: `0px ${INPUT_BOX_PADDING}px 4px` },
            contentStyle: { maxWidth: `${DIALOG_MAX_WIDTH}px` },
        },
        div: {
            style: {
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                height: `${IMAGE_HEIGHT}px`,
                position: "absolute",
                top: "20%",
                transform: "translateY(-25%)",
                width: "44px",
            },
        },
        iconButton: {
            style: {
                top: "27%",
                transform: "translateY(-50%)",
                margin: "0px 10px 0 0",
            },
            iconStyle: {
                color: theme.palette.error,
            },
        },
        imageIcon: {
            style: {
                left: "8px",
                top: "25%",
                transform: "translateY(-25%)",
            },
        },
        listItem: {
            style: {
                width: "100%",
            },
            secondaryTextDiv: {
                height: "70px",
                overflow: "hidden",
            },
        },
        viewFullDetail: {
            fontSize: "12px",
            position: postions.ABSOLUTE,
            right: "0px",
            bottom: "20%",
            color: theme.palette.darkestGrey,
        },
        popover: {
            anchorOrigin: { horizontal: "left", vertical: "bottom" },
            targetOrigin: { horizontal: "left", vertical: "top" },
            style: {
                // Note: Fix for popover jumping from top corner
                // https://github.com/mui-org/material-ui/issues/8040
                left: "-31415px",
            },
        },
    };
    componentDidUpdate(prevProps, prevState) {
        if (
            !prevState.filters.equals(this.state.filters) &&
            this.state.filters.has("__search") &&
            this.state.filters.get("__search") !== ""
        ) {
            this.handleDebouncedSearch();
        }
        if (prevProps.isFetching && !this.props.isFetching) {
            this.setState({
                globalProducts: this.mapProductstoListItems(this.props.globalProducts, { select: this.handleClick }),
            });
        }
        if (this.infiniteLoader && !prevProps.globalProducts.equals(this.props.globalProducts)) {
            this.infiniteLoader._registeredChild.Grid.forceUpdate();
        }
    }
    componentWillUnmount() {
        this.props.onResetGlobalProducts({ modal: true });
    }
    mapProductstoListItems = (products, { select, remove }: any) => {
        const { iconButton, imageIcon, listItem, viewFullDetail } = this.muiStyles;
        const { is_subtitle, is_title, is_text } = this.props.cardViewFields;
        if (products.size === 0) {
            return [
                <ListItem
                    key="0"
                    primaryText={formatMessage("No products found")}
                    secondaryText={formatMessage("Try using a different search term")}
                />,
            ];
        }
        return products
            .map((product) => {
                let style;
                const imageUrl = product.getIn(keyPath("image_url")) || product.getIn(keyPath("image_urls"));
                if (imageUrl) {
                    style = {
                        ...this.muiStyles.div.style,
                        backgroundImage: `url(${imageUrl}?max_dimension=${IMAGE_HEIGHT})`,
                    };
                }

                const secondaryTextDiv = (
                    <div style={listItem.secondaryTextDiv}>
                        {is_title ? <div> {product.getIn(is_title.path)} </div> : undefined}
                        {is_subtitle ? <div> {product.getIn(is_subtitle.path)} </div> : undefined}
                        {product.getIn(keyPath("color"), "") !== "" ? (
                            <div> {product.getIn(keyPath("color"), "")} </div>
                        ) : undefined}
                        {remove ? (
                            <Link
                                underline={"always"}
                                style={viewFullDetail}
                                href="#"
                                onClick={this.props.handleOpenIsViewFullDetail}
                            >
                                {formatMessage("View full details")}
                            </Link>
                        ) : undefined}
                    </div>
                );
                const itemSytle = remove
                    ? {
                          ...listItem.style,
                          borderBottom: `1px solid ${theme.palette.grey}`,
                          borderTop: `1px solid ${theme.palette.grey}`,
                      }
                    : listItem.style;
                return (
                    <ListItem
                        data-test={`product-${product.getIn(keyPath("_product_id"))}`}
                        leftIcon={style ? <div style={style} /> : <ImageIcon style={imageIcon.style} />}
                        key={product.getIn(keyPath("_product_id"))}
                        onClick={select ? select : undefined}
                        primaryText={is_text ? product.getIn(is_text.path) : ""}
                        rightIconButton={
                            remove ? (
                                <IconButton
                                    data-test={DATATEST.ASSORTMENTS.CDP.REMOVE_LINK}
                                    iconStyle={iconButton.iconStyle}
                                    onClick={remove}
                                    style={iconButton.style}
                                >
                                    <Trash />
                                </IconButton>
                            ) : undefined
                        }
                        secondaryText={secondaryTextDiv}
                        secondaryTextLines={2}
                        style={itemSytle}
                        value={product.getIn(keyPath("_product_id"))}
                    />
                );
            })
            .toJS();
    };
    handleClick = (event) => {
        const productId = event.currentTarget.getAttribute("value");
        let product = this.props.globalProducts.find((prod) => prod.getIn(keyPath("_product_id")) === productId);
        if (product) {
            product = this.mapProductstoListItems(List([product]), { remove: this.handleRemoveProduct });
        }
        this.props.setViewFullDetailProductId(productId);
        this.setState((state) => {
            return {
                isPopoverOpen: false,
                product,
                productId,
            };
        });
    };
    // tslint:disable-next-line
    handleDebouncedSearch = debounce(() => {
        const { brandId, itemDisplayProperty } = this.props;
        const filters = this.state.filters.toJS();
        const params: any = { limit: 10, modal: true, merchantId: this.props.merchantId };
        if (filters.__search) {
            params.search = {
                query: filters.__search,
                fields: itemDisplayProperty.map((schemaKey) => schemaKey.get("field_name")).toJS(),
            };
            delete filters.__search;
        }

        if (brandId) {
            params.filter = [
                {
                    in: {
                        field: "_brand_id",
                        values: [{ string_value: brandId }],
                    },
                },
            ];
        }

        this.props.onFetchGlobalProducts(params);
    }, 300);
    handleUpdateSearch = (event) => {
        const { value } = event.currentTarget;
        const shouldOpen = value.length > 2;
        this.setState((state) => ({
            filters: value ? state.filters.set("__search", value) : state.filters.delete("__search"),
            isPopoverOpen: shouldOpen,
        }));
    };
    handleSubmit = (data) => {
        const { globalProducts } = this.props;
        const { productId } = this.state;
        if (!productId) {
            return;
        }
        const productData: any = {
            fields: {
                product_id: { string_value: productId },
            },
        };
        let product = globalProducts.find((globalProduct) => globalProduct.getIn(keyPath("_product_id")) === productId);
        if (product) {
            product = product.toJS();
            productData.fields._cdn_images = { string_value: getValueByKey(product, "_cdn_images") };
            productData.fields._images = { string_value: getValueByKey(product, "_images") };
            productData.fields.image_url = { string_value: getValueByKey(product, "image_url") };
            productData.fields.image_urls = { string_value: getValueByKey(product, "image_urls") };
        }
        this.props.onSubmit(productData);
        this.setInitialState();
    };
    handleFormSubmit = () => {
        this._form.submit();
    };
    handleBindPopover = () => {
        this.setState((state) => {
            if (state.anchorEl && !state.filters.has("__search")) {
                return null;
            }
            return {
                ...state,
                anchorEl: !state.anchorEl ? this.container : state.anchorEl,
                isPopoverOpen: state.filters.has("__search"),
            };
        });
    };
    handleClosePopover = () => {
        this.setState({ isPopoverOpen: false });
    };
    handleOpenPopover = () => {
        this.setState({ isPopoverOpen: true });
    };

    handleRemoveProduct = () => {
        const { product, productId } = initialState();
        this.props.setViewFullDetailProductId("");
        this.setState({ product, productId });
    };
    isRowLoaded = ({ index }) => {
        return this.state.globalProducts[index];
    };
    loadMoreRows = () => {
        // Note: Preventing an unwanted a next fetch call here
        const { brandId } = this.props;
        if (this.props.globalProducts && this.props.globalProducts.size > 0) {
            const params = {
                modal: true,
                merchantId: this.props.merchantId,
            };

            if (brandId) {
                (params as any).filter = [
                    {
                        in: {
                            field: "_brand_id",
                            values: [{ string_value: brandId }],
                        },
                    },
                ];
            }

            if (
                this.props.globalProductState &&
                this.props.globalProductState.nextPageQueryModal &&
                this.props.globalProductState.nextPageQueryModal.scroll_id
            ) {
                return new Promise(() => {
                    this.props.onFetchNextGlobalProducts(params);
                });
            } else {
                // intentionally blank. No need to fetch another page
            }
        }
    };
    setInitialState = () => {
        this.setState(initialState());
    };
    renderRow = ({ key, index, parent, style }) => {
        const product = this.state.globalProducts[index];

        return product ? (
            <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                <div style={style}>{product}</div>
            </CellMeasurer>
        ) : null;
    };
    getListWidth = (defaultWidth) => {
        let returnWidth = defaultWidth;
        const windowWidth = window.innerWidth;
        if (window.innerWidth < DIALOG_PADDING + DIALOG_MAX_WIDTH) {
            returnWidth = windowWidth * 0.9 - INPUT_BOX_PADDING * 2;
        }
        return Math.max(returnWidth, 240);
    };

    render() {
        const { infiniteLoader, list } = REACT_VIRTUALIZED_CONSTANTS;
        const { dialog, popover }: any = this.muiStyles;
        const { onCancel } = this.props;
        const { anchorEl, filters, globalProducts, isPopoverOpen, product, productId } = this.state;
        const { __search = "" } = filters.toObject();
        const listWidth = this.getListWidth(list.width);
        return (
            <Dialog
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.CDP.CONFIRM_LINK_BTN}
                        disabled={!productId}
                        key="link"
                        label={formatMessage("Link")}
                        onClick={this.handleFormSubmit}
                        primary={true}
                    />,
                ]}
                bodyStyle={dialog.bodyStyle}
                contentStyle={dialog.contentStyle}
                onRequestClose={onCancel}
                open={true}
                title={formatMessage("Link Product")}
            >
                <div ref={this.bindRef}>
                    <Form
                        data-test={DATATEST.ASSORTMENTS.CDP.LINK_PRODUCT_FORM}
                        onValidSubmit={this.handleSubmit}
                        ref={this.bindFormRef}
                    >
                        {!isPopoverOpen && product}
                        {(isPopoverOpen || !product) && (
                            <TextField
                                dataTest={DATATEST.ASSORTMENTS.CDP.LINK_PRODUCT_INPUT}
                                defaultValue={__search}
                                fullWidth={true}
                                name="link-product"
                                onChange={this.handleUpdateSearch}
                                onFocus={this.handleBindPopover}
                                placeholder={formatMessage("Search for Products")}
                                maxLength={SEARCH_PRODUCTS_MAX_LENGTH}
                            />
                        )}
                    </Form>
                    <Popover
                        anchorEl={anchorEl}
                        anchorOrigin={popover.anchorOrigin}
                        animation={PopoverAnimationVertical}
                        canAutoPosition={true}
                        onRequestClose={this.handleClosePopover}
                        open={isPopoverOpen}
                        targetOrigin={popover.targetOrigin}
                        style={popover.style}
                    >
                        <InfiniteLoader
                            isRowLoaded={this.isRowLoaded}
                            loadMoreRows={this.loadMoreRows}
                            rowCount={infiniteLoader.rowCount}
                            ref={this.bindInfiniteLoader}
                            threshold={infiniteLoader.threshold}
                        >
                            {({ onRowsRendered, registerChild }) => (
                                <VirtualizedList
                                    ref={registerChild}
                                    deferredMeasurementCache={this.cache}
                                    rowCount={(globalProducts && globalProducts.length) || 0}
                                    rowRenderer={this.renderRow}
                                    height={list.height}
                                    width={listWidth}
                                    rowHeight={this.cache.rowHeight}
                                    onRowsRendered={onRowsRendered}
                                />
                            )}
                        </InfiniteLoader>
                    </Popover>
                </div>
            </Dialog>
        );
    }
    private bindRef = (ref) => (this.container = ref);
    private bindFormRef = (ref) => (this._form = ref);
    private bindInfiniteLoader = (ref) => (this.infiniteLoader = ref);
}
